/**
 * Euclidean Rhythm Generator
 * Based on Bjorklund's algorithm - distributes pulses evenly across steps
 *
 * Example patterns:
 * E(5,8) = [x.x.x.x.] - Cuban tresillo
 * E(3,8) = [x..x..x.] - Standard rock/pop
 * E(4,16) = [x...x...x...x...] - Four-on-floor kick
 */

/**
 * Generate Euclidean rhythm pattern
 * @param steps - Total number of steps in the pattern (typically 16)
 * @param pulses - Number of hits to distribute across steps
 * @param rotation - Rotate pattern by N steps (default 0)
 * @returns Boolean array where true = hit, false = rest
 */
export function euclideanRhythm(
  steps: number,
  pulses: number,
  rotation = 0
): boolean[] {
  // Ensure pulses doesn't exceed steps
  if (pulses > steps) {
    pulses = steps;
  }

  // Handle edge cases
  if (pulses === 0) {
    return new Array(steps).fill(false);
  }

  if (pulses === steps) {
    return new Array(steps).fill(true);
  }

  // Bjorklund's algorithm
  const pattern: boolean[] = [];
  const bucket: number[] = [];

  // Calculate cumulative distribution
  for (let i = 0; i < steps; i++) {
    bucket[i] = Math.floor((i * pulses) / steps);
  }

  // Determine where pulses fall
  for (let i = 0; i < steps; i++) {
    const current = Math.floor((i * pulses) / steps);
    const next = Math.floor(((i + 1) * pulses) / steps);
    pattern[i] = current !== next;
  }

  // Apply rotation
  if (rotation !== 0) {
    const normalizedRotation = ((rotation % steps) + steps) % steps;
    return [
      ...pattern.slice(normalizedRotation),
      ...pattern.slice(0, normalizedRotation),
    ];
  }

  return pattern;
}

/**
 * Convert Euclidean pattern to readable string
 * @param pattern - Boolean pattern array
 * @returns String representation (x = hit, . = rest)
 */
export function patternToString(pattern: boolean[]): string {
  return pattern.map((hit) => (hit ? 'x' : '.')).join('');
}

/**
 * Common Euclidean patterns used in music
 */
export const EUCLIDEAN_PATTERNS = {
  // Percussion patterns (16 steps)
  fourOnFloor: euclideanRhythm(16, 4, 0), // [x...x...x...x...]
  kick: euclideanRhythm(16, 4, 0), // Four-on-floor
  snare: euclideanRhythm(16, 2, 4), // On beats 2 and 4
  hihat: euclideanRhythm(16, 8, 0), // Eighth notes
  clap: euclideanRhythm(16, 3, 4), // Syncopated

  // Bass patterns (16 steps)
  bassQuarters: euclideanRhythm(16, 4, 0), // Quarter notes
  bassEighths: euclideanRhythm(16, 8, 0), // Eighth notes
  bassGroove: euclideanRhythm(16, 5, 0), // 5 hits distributed

  // Melodic patterns (16 steps)
  melodySparse: euclideanRhythm(16, 4, 0), // Sparse melody
  melodyMedium: euclideanRhythm(16, 6, 0), // Medium density
  melodyDense: euclideanRhythm(16, 12, 0), // Dense melody

  // Pad patterns (16 steps)
  padSustain: euclideanRhythm(16, 1, 0), // One long note per 16 steps
  padChords: euclideanRhythm(16, 2, 0), // Two chord changes
} as const;

/**
 * Generate random Euclidean pattern
 * @param steps - Number of steps
 * @param minPulses - Minimum pulses
 * @param maxPulses - Maximum pulses
 * @returns Random Euclidean pattern
 */
export function randomEuclideanPattern(
  steps: number,
  minPulses: number,
  maxPulses: number
): boolean[] {
  const pulses = Math.floor(Math.random() * (maxPulses - minPulses + 1)) + minPulses;
  const rotation = Math.floor(Math.random() * steps);
  return euclideanRhythm(steps, pulses, rotation);
}
